import React from "react";
import { mapIcon } from "src/helpers/map-icon";
import { Message, MessageSender, MessageDropdownState } from "../types";
import {
  renderWithCitations,
  stripCitationMarkers,
  SourcesFooter,
} from "./Citation";

interface MessageBubbleProps {
  message: Message;
  isLast: boolean;
  isMessageIncoming: boolean;
  botTypingMsg: string | null | undefined;
  userHasCommentPermission: boolean;
  queryType: string | null | undefined;
  messageDropdown: MessageDropdownState;
  latestMessageRef: React.RefObject<HTMLDivElement>;
  onDropdownToggle: (messageId: number, event: React.MouseEvent) => void;
  onDropdownClick: (action: string, messageId: number) => void;
  onEnlargeImage: (url: string) => void;
  onOpenDocument: (doc: string) => void;
  onCitationClick: (url: string) => void;
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isLast,
  isMessageIncoming,
  botTypingMsg,
  userHasCommentPermission,
  queryType,
  messageDropdown,
  latestMessageRef,
  onDropdownToggle,
  onDropdownClick,
  onEnlargeImage,
  onOpenDocument,
  onCitationClick,
}) => {
  // Build the message body. While the last bot message is still streaming,
  // we have to use dangerouslySetInnerHTML so the streaming chunk handler
  // (which writes directly to latestMessageRef.current.innerHTML between
  // React renders) doesn't get clobbered. Once the stream is settled, we can
  // swap in the React-tree render that splits citation markers into chips.
  //
  // We key off isMessageIncoming, not message.isProcessing — the latter is
  // the "Bot is thinking" indicator, which the chunk handler flips false on
  // the first text chunk. isMessageIncoming stays true for the full lifetime
  // of the SSE stream and only flips false on [DONE] / error / close.
  const isStreaming =
    isLast && message.sender === MessageSender.Bot && isMessageIncoming;
  const citationContent = !isStreaming
    ? renderWithCitations(message.message, message.sources, onCitationClick)
    : null;
  return (
    <div
      className={`p-1 flex ${
        message.sender === MessageSender.User && "justify-end"
      }`}
    >
      {message.isProcessing && (
        <div className="m-2 rounded-xl shadow-sm bg-white max-w-content w-max fade-in">
          <div className="p-2 flex flex-row max-w-content">
            <p className="text-blackinline-block text-left shrink">
              <span>{botTypingMsg || "Bot is thinking"}</span>
              <div className="blink-dot"></div>
              <div className="blink-dot"></div>
              <div className="blink-dot"></div>
            </p>
          </div>
        </div>
      )}
      {!message.isProcessing && (
        <div
          className={`m-1 rounded-xl shadow-sm w-max text-base ${
            message.sender === MessageSender.Bot
              ? "bg-white fly-in-right rounded-bl-none mr-5"
              : "bg-primary fly-in-left rounded-br-none text-white ml-5"
          }`}
        >
          <div
            className={`p-2 flex flex-row w-max-80 ${
              message.sender === MessageSender.Bot ? "mr-auto" : "ml-auto"
            }`}
          >
            <div className="flex flex-col">
              {message.sender === MessageSender.Bot &&
                message.id > 1 &&
                userHasCommentPermission &&
                (queryType === "VEC" || queryType === "SDQ") && (
                  <div className="flex flex-row justify-end w-full">
                    <button
                      className="px-2 py-1 rounded-md relative"
                      onClick={(e) => onDropdownToggle(message.id, e)}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        strokeWidth={1.5}
                        stroke="currentColor"
                        className="size-6"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 6.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 12.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5ZM12 18.75a.75.75 0 1 1 0-1.5.75.75 0 0 1 0 1.5Z"
                        />
                      </svg>
                    </button>

                    {messageDropdown.messageId === message.id &&
                      messageDropdown.position && (
                        <div
                          className="fixed bg-white rounded-md shadow-lg z-50 min-w-[150px]"
                          style={{
                            top: `${messageDropdown.position.y}px`,
                            left: `${messageDropdown.position.x}px`,
                            maxWidth: "calc(100vw - 20px)",
                            transform: "translateY(5px)",
                          }}
                        >
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={() =>
                              onDropdownClick("Add Comment", message.id)
                            }
                          >
                            Add Comment
                          </button>
                          <button
                            className="w-full text-left px-4 py-2 hover:bg-gray-100"
                            onClick={() =>
                              onDropdownClick("Edit Comments", message.id)
                            }
                          >
                            Edit Comments
                          </button>
                        </div>
                      )}
                  </div>
                )}
              {isLast && message.sender === MessageSender.Bot ? (
                isStreaming ? (
                  // Distinct key forces React to unmount this div on the
                  // streaming → done transition. Without it, React tries to
                  // reconcile the same div in place and can't remove child
                  // nodes that the chunk handler wrote via direct innerHTML
                  // mutation, throwing a NotFoundError on removeChild.
                  <div
                    key="bot-streaming"
                    ref={latestMessageRef}
                    className={
                      "inline-block text-left shrink px-2 break-words overflow-hidden"
                    }
                    dangerouslySetInnerHTML={{ __html: message.message }}
                  ></div>
                ) : (
                  <div
                    key="bot-done"
                    className={
                      "inline-block text-left shrink px-2 break-words overflow-hidden"
                    }
                  >
                    {citationContent ?? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html:
                            message.sender === MessageSender.Bot
                              ? stripCitationMarkers(message.message)
                              : message.message,
                        }}
                      />
                    )}
                  </div>
                )
              ) : (
                <div
                  className={`inline-block ${
                    message.sender === MessageSender.Bot
                      ? "text-left"
                      : "text-right"
                  } shrink grow px-2 break-words overflow-hidden`}
                >
                  {citationContent ?? (
                    <span
                      dangerouslySetInnerHTML={{
                        __html:
                          message.sender === MessageSender.Bot
                            ? stripCitationMarkers(message.message)
                            : message.message,
                      }}
                    />
                  )}
                </div>
              )}
              {message.sender === MessageSender.Bot && (
                <SourcesFooter
                  html={message.message}
                  sources={message.sources}
                  onClick={onCitationClick}
                />
              )}
              {message.imagesLoading && (
                <div className="flex justify-center mt-2">
                  <svg
                    aria-hidden="true"
                    className="w-7 h-7 text-gray-200 animate-spin fill-primary"
                    viewBox="0 0 100 101"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                      fill="none"
                    />
                    <path
                      d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                      fill="currentFill"
                    />
                  </svg>
                  <span className="sr-only">Loading...</span>
                </div>
              )}
              {message.images && message.images.length > 0 && (
                <div className="flex">
                  {message.images.map((image, index) => (
                    <div
                      key={index}
                      onClick={() =>
                        onEnlargeImage(
                          `https://sopai-images-dev.s3.us-east-2.amazonaws.com/${image.trim()}`,
                        )
                      }
                      className="cursor-pointer mt-2 hover:brightness-50 transition duration-75"
                    >
                      <img
                        src={`https://sopai-images-dev.s3.us-east-2.amazonaws.com/${image.trim()}`}
                        alt={image}
                        width={200}
                        height={200}
                      />
                    </div>
                  ))}
                </div>
              )}
              {message.docs.length > 0 && (
                <div className="flex flex-row w-full pl-2 mt-5 pr-5">
                  <div className="grid grid-cols-2 gap-2 w-full">
                    {message.docs.map((doc, index) => (
                      <button
                        key={index}
                        className="text-black text-left w-full pl-2 mr-5 bg-[#E8E5DA] rounded-lg p-1 h-14 flex items-center shadow-sm hover:shadow-lg"
                        onClick={() => onOpenDocument(doc)}
                      >
                        <img
                          src={mapIcon(
                            doc.split(".").pop()?.toLocaleLowerCase() || "",
                          )}
                          alt="doc"
                          width={40}
                          height={40}
                          className="mr-2"
                        />
                        <span className="truncate text-sm">{doc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
